// votes.js

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPredictedVotes, getMemberPredictedVote } from '../features/bills/billSlice';
import ReactMarkdown from 'react-markdown';
import { useReactToPrint } from 'react-to-print';

const Votes = () => {
  const billId = 'HB164';
  const session = '89R';
  const version = 'FIL';
  const dispatch = useDispatch();
  const predictedVotes = useSelector((state) => state.bill.predictedVotes);
  const memberPrediction = useSelector((state) => state.bill.memberPrediction);

  const [sortConfig, setSortConfig] = useState({ key: 'memnum', direction: 'ascending' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTab, setActiveTab] = useState('favor');
  const [confidenceRange, setConfidenceRange] = useState([0, 100]);

  const modalRef = useRef();

  const votes = predictedVotes || [];

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!votes || votes.length === 0) return null;

    const totalMembers = votes.length;
    const predictedYes = votes.filter((v) => v.yes > 50).length;
    const predictedNo = votes.filter((v) => v.no > 50).length;
    const avgConfidence = Math.round(votes.reduce((sum, v) => sum + Math.max(v.yes, v.no), 0) / totalMembers);

    const partyBreakdown = votes.reduce((acc, v) => {
      const party = v.party;
      if (!acc[party]) {
        acc[party] = { yes: 0, no: 0, total: 0 };
      }
      acc[party].total++;
      if (v.yes > 50) acc[party].yes++;
      else acc[party].no++;
      return acc;
    }, {});

    return {
      totalMembers,
      predictedYes,
      predictedNo,
      avgConfidence,
      partyBreakdown,
      yesPercentage: Math.round((predictedYes / totalMembers) * 100),
      noPercentage: Math.round((predictedNo / totalMembers) * 100),
    };
  }, [votes]);

  useEffect(() => {
    dispatch(getPredictedVotes({ billId, session, version }));
  }, [dispatch, billId, session, version]);

  useEffect(() => {
    if (memberPrediction) {
      console.log('Member Prediction:', memberPrediction);
    }
  }, [memberPrediction]);

  // Sorting and filtering function
  const sortedVotes = React.useMemo(() => {
    let sortableVotes = [...votes];

    // Filter by confidence range
    sortableVotes = sortableVotes.filter((vote) => {
      const confidence = Math.max(vote.yes, vote.no);
      return confidence >= confidenceRange[0] && confidence <= confidenceRange[1];
    });

    // Sort
    if (sortConfig !== null) {
      sortableVotes.sort((a, b) => {
        let aKey = a[sortConfig.key];
        let bKey = b[sortConfig.key];

        // If sorting by 'yes', ensure numerical comparison
        if (sortConfig.key === 'yes') {
          aKey = Number(aKey);
          bKey = Number(bKey);
        } else {
          aKey = aKey.toString().toLowerCase();
          bKey = bKey.toString().toLowerCase();
        }

        if (aKey < bKey) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aKey > bKey) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableVotes;
  }, [votes, sortConfig, confidenceRange]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽';
    }
    return '';
  };

  const handleRangeChange = (e, type) => {
    const value = parseInt(e.target.value);
    if (type === 'min') {
      setConfidenceRange([Math.min(value, confidenceRange[1]), confidenceRange[1]]);
    } else {
      setConfidenceRange([confidenceRange[0], Math.max(value, confidenceRange[0])]);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
    setActiveTab('favor');
  };

  const handleMemberClick = (memnum) => {
    setSelectedMember(memnum);
    dispatch(getMemberPredictedVote({ billId, session, version, memnum }));
    setIsModalOpen(true);
    setActiveTab('favor');
  };

  // Parse prediction text into sections
  const parsePrediction = (predictionText) => {
    if (!predictionText) return { favor: '', against: '' };

    // Remove the first line if it matches Yes-X/No-Y pattern
    let text = predictionText.trim();
    const lines = text.split('\n');
    if (lines[0] && lines[0].match(/^(Yes|No)-\d+\/(Yes|No)-\d+$/i)) {
      lines.shift(); // Remove first line
      text = lines.join('\n').trim();
    }

    // Split by "Reasons in Favor:" and "Reasons Against:"
    const favorMatch = text.match(/Reasons in Favor:([\s\S]*?)(?=Reasons Against:|$)/i);
    const againstMatch = text.match(/Reasons Against:([\s\S]*?)$/i);

    return {
      favor: favorMatch ? favorMatch[1].trim() : text,
      against: againstMatch ? againstMatch[1].trim() : '',
    };
  };

  // Setup react-to-print
  const handlePrint = useReactToPrint({
    content: () => modalRef.current,
    documentTitle: `Member_Prediction_${selectedMember}`,
    onAfterPrint: () => console.log('Print Success'),
  });

  return (
    <div className="vote-page-container">
      <div className="vote-page-header">
        <h1>Member Vote Predictions</h1>
        <p className="vote-subtitle">
          AI-powered predictions for {billId} - Session {session}
        </p>
      </div>

      {summaryStats && (
        <div className="vote-summary-dashboard">
          <div className="summary-main">
            <div className="summary-split-layout">
              <div className="summary-left">
                <div
                  className={`outcome-badge ${summaryStats.predictedYes > summaryStats.predictedNo ? 'likely-pass' : 'likely-fail'}`}
                >
                  {summaryStats.predictedYes > summaryStats.predictedNo ? 'LIKELY TO PASS' : 'LIKELY TO FAIL'}
                </div>
                <div className="outcome-stats-stacked">
                  <div className="outcome-stat yes-outcome">
                    <span className="outcome-count">{summaryStats.predictedYes}</span>
                    <span className="outcome-label">Predicted Yes</span>
                  </div>
                  <div className="outcome-divider">vs</div>
                  <div className="outcome-stat no-outcome">
                    <span className="outcome-count">{summaryStats.predictedNo}</span>
                    <span className="outcome-label">Predicted No</span>
                  </div>
                </div>
                <div className="summary-visual">
                  <svg className="summary-donut" viewBox="0 0 200 200">
                    <circle className="donut-bg" cx="100" cy="100" r="80" />
                    <circle
                      className="donut-yes"
                      cx="100"
                      cy="100"
                      r="80"
                      strokeDasharray={`${summaryStats.yesPercentage * 5.03} 502.65`}
                      transform="rotate(-90 100 100)"
                    />
                    <circle
                      className="donut-no"
                      cx="100"
                      cy="100"
                      r="80"
                      strokeDasharray={`${summaryStats.noPercentage * 5.03} 502.65`}
                      strokeDashoffset={`-${summaryStats.yesPercentage * 5.03}`}
                      transform="rotate(-90 100 100)"
                    />
                    <text x="100" y="95" textAnchor="middle" className="donut-percentage">
                      {summaryStats.yesPercentage}%
                    </text>
                    <text x="100" y="115" textAnchor="middle" className="donut-label">
                      Yes Votes
                    </text>
                  </svg>
                </div>
              </div>

              <div className="summary-right">
                <h3 className="scatter-title">Member Predictions Map</h3>
                <div className="scatter-plot">
                  <div className="scatter-axes">
                    <div className="axis-label y-axis">High Confidence</div>
                  </div>
                  <svg className="scatter-svg" viewBox="0 0 500 150">
                    <defs>
                      {votes.map((vote) => (
                        <pattern
                          key={`pattern-${vote.memnum}`}
                          id={`member-img-${vote.memnum}`}
                          x="0"
                          y="0"
                          width="1"
                          height="1"
                        >
                          <image
                            href={`https://legisconstorage.blob.core.windows.net/memberimages/${vote.memnum}.jpg`}
                            x="0"
                            y="0"
                            width="36"
                            height="36"
                            preserveAspectRatio="xMidYMid slice"
                            onError={(e) => {
                              // Fallback to a placeholder if image fails to load
                              e.target.href.baseVal = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzYiIGhlaWdodD0iMzYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjM2IiBoZWlnaHQ9IjM2IiBmaWxsPSIjZTJlOGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzRhNTU2OCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPj88L3RleHQ+PC9zdmc+';
                            }}
                          />
                        </pattern>
                      ))}
                    </defs>

                    {/* Grid lines */}
                    <line x1="250" y1="10" x2="250" y2="140" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4,4" />

                    {/* Plot members */}
                    {votes.map((vote) => {
                      const confidence = Math.max(vote.yes, vote.no);
                      const x = 50 + vote.yes * 4;
                      // Map confidence from 50-100% range to full chart height (10-140)
                      const y = 140 - (confidence - 50) * 2.6;

                      return (
                        <g key={vote.memnum}>
                          <circle
                            cx={x}
                            cy={y}
                            r="18"
                            fill={`url(#member-img-${vote.memnum})`}
                            stroke={vote.yes > 50 ? '#48bb78' : '#f56565'}
                            strokeWidth="3"
                            className="scatter-dot"
                            onClick={() => handleMemberClick(vote.memnum)}
                            style={{ cursor: 'pointer' }}
                          />
                          <title>
                            Member #{vote.memnum} - {vote.yes > 50 ? 'Yes' : 'No'} ({confidence}% confidence)
                          </title>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="summary-stats-grid">
            <div className="stat-box">
              <div className="stat-box-icon">👥</div>
              <div className="stat-box-content">
                <div className="stat-box-value">{summaryStats.totalMembers}</div>
                <div className="stat-box-label">Total Members</div>
              </div>
            </div>
            <div className="stat-box">
              <div className="stat-box-icon">🎯</div>
              <div className="stat-box-content">
                <div className="stat-box-value">{summaryStats.avgConfidence}%</div>
                <div className="stat-box-label">Avg Confidence</div>
              </div>
            </div>
            {Object.entries(summaryStats.partyBreakdown).map(([party, data]) => (
              <div key={party} className="stat-box party-stat">
                <div className={`stat-box-icon party-icon-${party.toLowerCase()}`}>{party}</div>
                <div className="stat-box-content">
                  <div className="stat-box-value">
                    {data.yes}/{data.total}
                  </div>
                  <div className="stat-box-label">Yes Votes</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="vote-controls-combined">
        <div className="sort-section">
          <div className="sort-controls">
            <button
              className={`sort-btn ${sortConfig.key === 'memnum' ? 'active' : ''}`}
              onClick={() => requestSort('memnum')}
            >
              Member {getSortIndicator('memnum')}
            </button>
            <button
              className={`sort-btn ${sortConfig.key === 'party' ? 'active' : ''}`}
              onClick={() => requestSort('party')}
            >
              Party {getSortIndicator('party')}
            </button>
            <button
              className={`sort-btn ${sortConfig.key === 'yes' ? 'active' : ''}`}
              onClick={() => requestSort('yes')}
            >
              Confidence {getSortIndicator('yes')}
            </button>
          </div>
        </div>

        <div className="confidence-filter-inline">
          <div className="filter-header-inline">
            <span className="filter-title-inline">Confidence Range</span>
            <span className="filter-values-inline">
              {confidenceRange[0]}% - {confidenceRange[1]}%
            </span>
          </div>
          <div className="range-slider-container-inline">
            <input
              type="range"
              min="0"
              max="100"
              value={confidenceRange[0]}
              onChange={(e) => handleRangeChange(e, 'min')}
              className="range-slider range-slider-min"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={confidenceRange[1]}
              onChange={(e) => handleRangeChange(e, 'max')}
              className="range-slider range-slider-max"
            />
            <div className="range-track">
              <div
                className="range-track-fill"
                style={{
                  left: `${confidenceRange[0]}%`,
                  width: `${confidenceRange[1] - confidenceRange[0]}%`,
                }}
              />
            </div>
          </div>
          <div className="filter-info-inline">
            Showing {sortedVotes.length} of {votes.length} members
          </div>
        </div>
      </div>

      <div className="vote-cards-grid">
        {sortedVotes.map((vote) => (
          <div key={vote.memnum} className="vote-card" onClick={() => handleMemberClick(vote.memnum)}>
            <div className="vote-card-header">
              <div className="member-info">
                <span className="member-number">#{vote.memnum}</span>
                <span className={`party-badge party-${vote.party.toLowerCase()}`}>{vote.party}</span>
              </div>
            </div>

            <div className="vote-card-body">
              <div className="prediction-visual">
                <svg className="circular-progress" viewBox="0 0 120 120">
                  <circle className="progress-background" cx="60" cy="60" r="54" />
                  <circle
                    className="progress-yes"
                    cx="60"
                    cy="60"
                    r="54"
                    strokeDasharray={`${vote.yes * 3.39} 339.29`}
                    transform="rotate(-90 60 60)"
                  />
                  <circle
                    className="progress-no"
                    cx="60"
                    cy="60"
                    r="54"
                    strokeDasharray={`${vote.no * 3.39} 339.29`}
                    strokeDashoffset={`-${vote.yes * 3.39}`}
                    transform="rotate(-90 60 60)"
                  />
                </svg>
                <div className="prediction-center">
                  <div className={`prediction-result ${vote.yes > 50 ? 'yes-prediction' : 'no-prediction'}`}>
                    {vote.yes > 50 ? 'YES' : 'NO'}
                  </div>
                  <div className="prediction-confidence">{Math.max(vote.yes, vote.no)}%</div>
                </div>
              </div>

              <div className="vote-breakdown">
                <div className="breakdown-item yes-item">
                  <div className="breakdown-label">Yes</div>
                  <div className="breakdown-value">{vote.yes}%</div>
                </div>
                <div className="breakdown-divider"></div>
                <div className="breakdown-item no-item">
                  <div className="breakdown-label">No</div>
                  <div className="breakdown-value">{vote.no}%</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && memberPrediction && (
        <div className="vote-modal-overlay" onClick={closeModal}>
          <div className="vote-modal-content" onClick={(e) => e.stopPropagation()} ref={modalRef}>
            <button className="vote-modal-close" onClick={closeModal}>
              &times;
            </button>

            <div className="vote-modal-header">
              <h2>Member #{selectedMember} Prediction</h2>
              <button className="vote-modal-print" onClick={handlePrint}>
                Print Analysis
              </button>
            </div>

            <div className="vote-modal-prediction">
              <div className="modal-circular-progress">
                <svg viewBox="0 0 200 200">
                  <circle className="modal-progress-bg" cx="100" cy="100" r="85" />
                  <circle
                    className="modal-progress-yes"
                    cx="100"
                    cy="100"
                    r="85"
                    strokeDasharray={`${memberPrediction.yes * 5.34} 534.07`}
                    transform="rotate(-90 100 100)"
                  />
                  <circle
                    className="modal-progress-no"
                    cx="100"
                    cy="100"
                    r="85"
                    strokeDasharray={`${memberPrediction.no * 5.34} 534.07`}
                    strokeDashoffset={`-${memberPrediction.yes * 5.34}`}
                    transform="rotate(-90 100 100)"
                  />
                </svg>
                <div className="modal-prediction-text">
                  <div className={`modal-vote-result ${memberPrediction.yes > 50 ? 'yes-vote' : 'no-vote'}`}>
                    {memberPrediction.yes > 50 ? 'YES' : 'NO'}
                  </div>
                  <div className="modal-confidence-text">
                    {Math.max(memberPrediction.yes, memberPrediction.no)}% confidence
                  </div>
                </div>
              </div>

              <div className="modal-breakdown-stats">
                <div className="modal-stat-card yes-stat">
                  <div className="stat-icon">✓</div>
                  <div className="stat-content">
                    <div className="stat-label">Yes Vote</div>
                    <div className="stat-value">{memberPrediction.yes}%</div>
                  </div>
                </div>
                <div className="modal-stat-card no-stat">
                  <div className="stat-icon">✕</div>
                  <div className="stat-content">
                    <div className="stat-label">No Vote</div>
                    <div className="stat-value">{memberPrediction.no}%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="vote-modal-explanation">
              <div className="explanation-tabs">
                <button
                  className={`explanation-tab ${activeTab === 'favor' ? 'active' : ''}`}
                  onClick={() => setActiveTab('favor')}
                >
                  Reasons in Favor
                </button>
                <button
                  className={`explanation-tab ${activeTab === 'against' ? 'active' : ''}`}
                  onClick={() => setActiveTab('against')}
                >
                  Reasons Against
                </button>
              </div>
              <div className="explanation-content">
                {(() => {
                  const { favor, against } = parsePrediction(memberPrediction.prediction);
                  return activeTab === 'favor' ? (
                    <div className="tab-content favor-content">
                      <ReactMarkdown>{favor}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="tab-content against-content">
                      <ReactMarkdown>{against || 'No reasons against available.'}</ReactMarkdown>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Votes;
